/** Estado do board: uma consulta e duas escritas.
 *
 * A movimentação é **otimista** (DESIGN.md § 7): arrastar um card e esperar a
 * rede para vê-lo chegar faz a interface parecer travada. O card muda de
 * coluna na hora; se o servidor recusar, ele volta e o erro aparece.
 *
 * A criação **não** é otimista, e a diferença é deliberada: o board é um read
 * model alimentado por evento, então a oportunidade recém-criada só aparece
 * quando o projetor a processar. Fingir que ela já está lá produziria um card
 * que some no próximo `refetch` — pior que esperar.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
  type UseQueryResult,
} from "@tanstack/react-query";

import { useCallback, useEffect, useState } from "react";

import {
  buscarMaisDaColuna,
  buscarMetricas,
  buscarPessoas,
  buscarQuadro,
  criarOportunidade,
  moverOportunidade,
  perderOportunidade,
  type Card,
  type FiltrosDoQuadro,
  type Indicadores,
  type NovaOportunidade,
  type Pessoa,
  type Quadro,
} from "./api";

export const CHAVE_DO_QUADRO = ["quadro"] as const;
export const CHAVE_DAS_METRICAS = ["quadro", "metricas"] as const;

/** O projetor é assíncrono. Sem uma releitura periódica, um card criado por um
 * colega — ou pelo próprio projetor, com atraso — não apareceria até alguém
 * recarregar a página. */
const INTERVALO_DE_RELEITURA_MS = 20_000;

/** Com o stream de eventos conectado (SPEC-FE-001 § 3).
 *
 * Um minuto, e não "nunca": o aviso é pub/sub, que não guarda o que ninguém
 * escutou. Um Redis que piscou entre a publicação e a leitura faz um aviso
 * sumir sem erro em lugar nenhum — e sem esta rede o board ficaria errado até
 * a próxima mudança, que pode ser amanhã. */
const INTERVALO_COM_STREAM_MS = 60_000;

/** Enquanto se espera um card recém-criado aparecer.
 *
 * Medido em produção: a oportunidade fica pronta no servidor em **0,78 s** (o
 * publicador do outbox leva 0,14 s). Com o intervalo normal, a pessoa esperava
 * até 20 s por algo que já existia em menos de um — a releitura que acontece
 * logo após o `POST` chega cedo demais, e a seguinte só vem no próximo ciclo.
 */
const INTERVALO_AGUARDANDO_MS = 1_000;

/** Teto da espera curta.
 *
 * Sem ele, um projetor parado faria a tela bater no servidor a cada segundo
 * para sempre. Passado o teto, volta ao ritmo normal e o aviso de atraso da
 * projeção assume — que é a informação correta nesse caso.
 */
const ESPERA_MAXIMA_MS = 30_000;

/** O que a projeção ainda precisa refletir para a tela voltar a confiar nela.
 *
 * O board é read model alimentado por evento: a escrita termina antes de a
 * projeção existir. Reler imediatamente devolve o estado **anterior**, e foi
 * isso que produziu dois sintomas que pareciam diferentes — o card que voltava
 * para a coluna de origem e precisava ser arrastado duas vezes, e o
 * responsável que continuava o antigo depois de salvo.
 */
export interface Aguardando {
  /** Id da oportunidade. */
  id: string;
  /** Quando a espera começou, em epoch ms. */
  desde: number;
  /** Etapa em que o card precisa aparecer. Ausente na criação, onde basta ele
   * existir — não há etapa esperada, qualquer uma serve. */
  paraEtapa?: string;
  /** Responsável que o card precisa mostrar. `undefined` quando não se espera
   * troca; `null` é espera legítima — tirar o responsável. */
  paraResponsavel?: string | null;
  /** A etapa de destino é de perda.
   *
   * Sem isto, o card segurado na coluna de perda voltava a exibir a etiqueta
   * verde "Quente" a cada releitura anterior à projeção: o servidor ainda
   * devolve `status: "open"`, e é do status que a temperatura sai.
   */
  perdida?: boolean;
}

/** A projeção já reflete o que se espera dela?
 *
 * Um só predicado para os três casos porque a decisão é a mesma: enquanto for
 * falso, a tela não pode tratar o que o servidor devolve como verdade nova.
 */
export function chegou(quadro: Quadro, aguardando: Aguardando): boolean {
  for (const coluna of quadro.columns) {
    const card = coluna.cards.find((c) => c.id === aguardando.id);
    if (card === undefined) continue;
    if (aguardando.paraEtapa !== undefined && coluna.stage_id !== aguardando.paraEtapa)
      return false;
    if (aguardando.paraResponsavel !== undefined && card.owner_id !== aguardando.paraResponsavel) {
      return false;
    }
    return true;
  }
  return false;
}

/** Quanto tempo até a próxima leitura do board.
 *
 * Função pura e exportada porque é onde mora a decisão — testá-la não exige
 * relógio falso nem esperar vinte segundos num teste.
 */
export function intervaloDeReleitura(
  quadro: Quadro | undefined,
  esperas: readonly Aguardando[],
  agora: number,
  emTempoReal = false,
): number {
  const pendente = esperas.some((espera) => {
    if (agora - espera.desde >= ESPERA_MAXIMA_MS) return false;
    return quadro === undefined || !chegou(quadro, espera);
  });
  if (pendente) return INTERVALO_AGUARDANDO_MS;
  // Com o stream de pé, o relógio vira rede de segurança e não o mecanismo:
  // quem manda reler é o projetor, no instante em que a projeção fica pronta.
  // **Não é zero**, de propósito. O aviso é entrega no melhor esforço — pub/sub
  // não guarda o que ninguém escutou, e o Redis pode ter piscado entre a
  // publicação e a leitura. Uma releitura de minuto em minuto custa quase nada
  // e é o que impede o board de ficar errado em silêncio quando um único aviso
  // se perde.
  return emTempoReal ? INTERVALO_COM_STREAM_MS : INTERVALO_DE_RELEITURA_MS;
}

export function contemCard(quadro: Quadro, id: string): boolean {
  return quadro.columns.some((coluna) => coluna.cards.some((card) => card.id === id));
}

/** A tela ainda deve fabricar o card na etapa de destino?
 *
 * Segurar o card é **mentir com prazo**: enquanto a projeção não alcança, a
 * tela mostra o que o servidor já aceitou. O prazo é o mesmo teto da releitura
 * rápida — sem ele, um projetor parado (ou uma espera que nunca vai se cumprir)
 * deixa a mentira permanente, e o funil exibe um estado que não existe até
 * alguém mover outro card ou recarregar a página.
 */
export function deveSegurar(
  quadro: Quadro,
  aguardando: Aguardando | undefined,
  agora: number,
): boolean {
  if (aguardando?.paraEtapa === undefined) return false;
  if (agora - aguardando.desde >= ESPERA_MAXIMA_MS) return false;
  return !chegou(quadro, aguardando);
}

/** Aplica ao quadro todas as esperas que ainda valem.
 *
 * Uma espera por card, e não uma só para a tela: com um slot único, mover dois
 * cards em sequência descartava a espera do primeiro, e ele voltava sozinho
 * para a coluna de origem na releitura seguinte — o defeito de `d9b9a63` de
 * volta para quem trabalha rápido.
 */
export function comEsperas(quadro: Quadro, esperas: readonly Aguardando[], agora: number): Quadro {
  return esperas.reduce((parcial, espera) => {
    if (espera.paraEtapa === undefined || !deveSegurar(parcial, espera, agora)) return parcial;

    const pedido = { cardId: espera.id, paraEtapa: espera.paraEtapa };
    return espera.perdida === true ? perder(parcial, pedido) : mover(parcial, pedido);
  }, quadro);
}

export function useQuadro(
  filtros: FiltrosDoQuadro = {},
  esperas: readonly Aguardando[] = [],
  emTempoReal = false,
): UseQueryResult<Quadro> {
  return useQuery({
    queryKey: [...CHAVE_DO_QUADRO, filtros.busca ?? "", filtros.ownerId ?? ""],
    queryFn: () => buscarQuadro(filtros),
    refetchInterval: (query) =>
      intervaloDeReleitura(query.state.data, esperas, Date.now(), emTempoReal),
    // Segura o card na coluna de destino enquanto a projeção não alcança.
    //
    // Sem isto, a releitura que acontece logo após o `PATCH` devolve o card na
    // coluna **antiga** — a projeção ainda não rodou —, e ele volta na frente
    // de quem acabou de arrastá-lo. A pessoa arrasta de novo, e da segunda vez
    // "funciona" porque a projeção da primeira já chegou. Era o que fazia todo
    // movimento precisar de dois gestos.
    //
    // Só para a etapa: no responsável não há como fabricar o nome que a
    // projeção vai trazer, e ali a releitura rápida basta — o card corrige em
    // cerca de um segundo em vez de esperar o ciclo de vinte.
    //
    // Com prazo, e o mesmo da releitura rápida: quem registra a espera é a
    // tela, e uma espera que nunca se cumpre — movimento recusado, projetor
    // parado — deixaria o card na coluna de destino para sempre.
    select: (quadro) => comEsperas(quadro, esperas, Date.now()),
    staleTime: 5_000,
  });
}

/** O que já foi carregado além da primeira página, por coluna. */
export interface ExtrasDaColuna {
  cards: Card[];
  next_cursor: string | null;
}

export type ExtrasPorColuna = Readonly<Record<string, ExtrasDaColuna>>;

/** Junta ao quadro as páginas extras já carregadas.
 *
 * **Fora do cache da consulta, e não dentro dele.** O board relê sozinho a cada
 * ciclo (o projetor é assíncrono), e a releitura devolve a primeira página —
 * gravar os extras no cache os faria sumir na próxima leitura, na frente de
 * quem acabou de pedir "carregar mais".
 *
 * A deduplicação por `id` não é zelo excessivo: entre carregar a página 2 e o
 * board reler, um card pode ter sido movido para esta coluna e passar a caber
 * na primeira página. Sem dedup ele apareceria duas vezes, e o React reclamaria
 * da chave repetida — depois de a pessoa já ter visto o card em duplicata.
 */
export function comExtras(quadro: Quadro, extras: ExtrasPorColuna): Quadro {
  if (Object.keys(extras).length === 0) return quadro;

  return {
    ...quadro,
    columns: quadro.columns.map((coluna) => {
      const extra = extras[coluna.stage_id];
      if (extra === undefined) return coluna;

      const jaNaPrimeira = new Set(coluna.cards.map((card) => card.id));
      return {
        ...coluna,
        cards: [...coluna.cards, ...extra.cards.filter((card) => !jaNaPrimeira.has(card.id))],
        next_cursor: extra.next_cursor,
      };
    }),
  };
}

/** O "carregar mais" de cada coluna (SPEC-OPP-020 § 3).
 *
 * Os extras são zerados quando o filtro muda: uma página 2 pedida com um
 * filtro e mostrada sob outro seria uma lista que nunca existiu.
 */
export function useMaisDaColuna(filtros: FiltrosDoQuadro = {}) {
  const [extras, setExtras] = useState<ExtrasPorColuna>({});
  const [carregando, setCarregando] = useState<string | null>(null);
  const chaveDoFiltro = `${filtros.busca ?? ""}|${filtros.ownerId ?? ""}`;

  useEffect(() => {
    setExtras({});
  }, [chaveDoFiltro]);

  const carregarMais = useCallback(
    async (stageId: string, cursor: string) => {
      setCarregando(stageId);
      try {
        const pagina = await buscarMaisDaColuna(stageId, cursor, filtros);
        setExtras((atuais) => {
          const anterior = atuais[stageId];
          return {
            ...atuais,
            [stageId]: {
              cards: [...(anterior?.cards ?? []), ...pagina.cards],
              next_cursor: pagina.next_cursor,
            },
          };
        });
      } finally {
        setCarregando(null);
      }
    },
    // `filtros` é literal de objeto no chamador: depender dele recriaria a
    // função a cada render. A chave textual é o que de fato muda a busca.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chaveDoFiltro],
  );

  return { extras, carregarMais, carregando };
}

/** Os indicadores do topo (SPEC-OPP-022).
 *
 * Consulta própria, e não parte de `useQuadro`: a busca digitada muda a chave
 * do quadro a cada tecla, e os indicadores não dependem do texto buscado —
 * juntos, cada letra digitada refaria o fechamento do mês.
 *
 * A chave começa por `quadro` de propósito: mover ou criar uma oportunidade
 * invalida `CHAVE_DO_QUADRO` por prefixo, e os números precisam acompanhar o
 * board em vez de continuar exibindo o total anterior.
 */
export function useMetricas(
  esperas: readonly Aguardando[] = [],
  emTempoReal = false,
): UseQueryResult<Indicadores> {
  return useQuery({
    queryKey: CHAVE_DAS_METRICAS,
    queryFn: () => buscarMetricas(),
    // **As mesmas esperas do board, e pelo mesmo motivo.** Os números saem do
    // read model, como as colunas: entre gravar e a projeção alcançar existe
    // uma janela, e a releitura disparada pela criação responde de dentro dela.
    // O board atravessa essa janela relendo rápido; a barra relia de vinte em
    // vinte segundos e ficava com a resposta velha.
    //
    // O efeito era ver, na mesma tela e ao mesmo tempo, "3 oportunidades
    // ativas" no topo e **4 cards** embaixo — dois números que discordam, e o
    // de cima é o que a pessoa lê primeiro. Foi o que a inspeção visual de
    // 15/08 encontrou.
    //
    // `undefined` no lugar do quadro porque aqui não há como verificar se a
    // espera chegou: um total não diz quais cards o compõem. Enquanto a espera
    // estiver de pé e dentro do prazo, relê rápido — e o prazo é o que impede
    // uma espera que nunca se cumpre de deixar a barra relendo para sempre.
    refetchInterval: () => intervaloDeReleitura(undefined, esperas, Date.now(), emTempoReal),
    staleTime: 5_000,
  });
}

/** As pessoas do tenant, para o seletor de responsável (SPEC-IDN-013).
 *
 * Cache longo: a lista de colegas muda em escala de semanas, e refazer a
 * consulta a cada abertura do formulário não traria nada.
 */
export function usePessoas(): UseQueryResult<Pessoa[]> {
  return useQuery({
    queryKey: ["pessoas"],
    queryFn: () => buscarPessoas(),
    staleTime: 5 * 60_000,
  });
}

export function useCriarOportunidade() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (dados: NovaOportunidade) => criarOportunidade(dados),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVE_DO_QUADRO }),
  });
}

export interface PedidoDeMovimento {
  cardId: string;
  paraEtapa: string;
}

/** Os quadros que estão no cache, com o snapshot de cada um.
 *
 * Filtra pelo **formato**, e não só pelo prefixo da chave. `CHAVE_DAS_METRICAS`
 * começa por `quadro` de propósito — é o que faz mover ou criar uma
 * oportunidade invalidar os indicadores junto —, mas `getQueriesData` casa por
 * prefixo e devolvia o `Indicadores` no mesmo lote. Entregue ao `mover`, ele
 * não tem `columns`, e o `TypeError` acontecia **dentro do `onMutate`**: no
 * TanStack Query v5 isso mata a mutação antes de `mutationFn`, então nenhuma
 * requisição chegava a sair. Da tela via-se "não foi possível mover" sem que o
 * servidor jamais tivesse sido consultado.
 */
function quadrosNoCache(cliente: QueryClient): [QueryKey, Quadro][] {
  const pares: [QueryKey, Quadro][] = [];
  for (const [chave, dados] of cliente.getQueriesData<Quadro>({ queryKey: CHAVE_DO_QUADRO })) {
    if (dados !== undefined && Array.isArray(dados.columns)) pares.push([chave, dados]);
  }
  return pares;
}

export function useMoverOportunidade() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ cardId, paraEtapa }: PedidoDeMovimento) => moverOportunidade(cardId, paraEtapa),

    onMutate: async (pedido) => {
      // Cancelar antes de mexer no cache: uma releitura em voo chegaria depois
      // e sobrescreveria o movimento otimista com o estado antigo.
      await cliente.cancelQueries({ queryKey: CHAVE_DO_QUADRO });

      const anteriores = quadrosNoCache(cliente);
      for (const [chave, quadro] of anteriores) {
        cliente.setQueryData(chave, mover(quadro, pedido));
      }
      return { anteriores };
    },

    onError: (_erro, _pedido, contexto) => {
      // O card volta para onde estava. Deixá-lo na coluna nova depois de o
      // servidor recusar seria mostrar um funil que não existe.
      for (const [chave, quadro] of contexto?.anteriores ?? []) {
        cliente.setQueryData(chave, quadro);
      }
    },

    onSettled: () => cliente.invalidateQueries({ queryKey: CHAVE_DO_QUADRO }),
  });
}

export interface PedidoDePerda {
  cardId: string;
  paraEtapa: string;
  motivo: string;
  observacao?: string;
}

/** Perder é fechar, e fechar exige motivo (SPEC-OPP-040).
 *
 * Rota própria, e não `PATCH /stage`: o domínio recusa marcar como perdida sem
 * `lost_reason_code`, e é o motivo que permite descobrir depois por que se
 * perde. Otimista como a movimentação, e pela mesma razão — o card sai da
 * coluna na hora, e volta se o servidor recusar.
 */
export function usePerderOportunidade() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ cardId, motivo, observacao }: PedidoDePerda) =>
      perderOportunidade(cardId, motivo, observacao),

    onMutate: async (pedido) => {
      await cliente.cancelQueries({ queryKey: CHAVE_DO_QUADRO });
      const anteriores = quadrosNoCache(cliente);
      for (const [chave, quadro] of anteriores) {
        cliente.setQueryData(
          chave,
          perder(quadro, { cardId: pedido.cardId, paraEtapa: pedido.paraEtapa }),
        );
      }
      return { anteriores };
    },

    onError: (_erro, _pedido, contexto) => {
      for (const [chave, quadro] of contexto?.anteriores ?? []) {
        cliente.setQueryData(chave, quadro);
      }
    },

    onSettled: () => cliente.invalidateQueries({ queryKey: CHAVE_DO_QUADRO }),
  });
}

/** O otimista da perda: o card muda de coluna **e** de desfecho.
 *
 * Reutilizar `mover` sozinho deixava o card recém-perdido com a etiqueta verde
 * "Quente" na coluna de perda — `mover` força `hot` para card aberto, e o
 * `status` só vira `lost` quando a projeção chega. A etiqueta pedia ação num
 * negócio que a pessoa acabou de encerrar.
 *
 * O status muda **antes** do movimento, e não depois, porque é dele que `mover`
 * decide a temperatura.
 */
export function perder(quadro: Quadro, pedido: PedidoDeMovimento): Quadro {
  return mover(
    {
      ...quadro,
      columns: quadro.columns.map((coluna) => ({
        ...coluna,
        cards: coluna.cards.map((card) =>
          card.id === pedido.cardId ? { ...card, status: "lost" as const } : card,
        ),
      })),
    },
    pedido,
  );
}

/** Move o card entre colunas e reajusta os totais.
 *
 * Os totais entram no cálculo porque eles são o número que o time olha para
 * saber quanto tem em jogo (SPEC-OPP-020 § 4). Mover o card sem mover o valor
 * deixaria a soma errada até a próxima releitura.
 */
export function mover(quadro: Quadro, { cardId, paraEtapa }: PedidoDeMovimento): Quadro {
  const origem = quadro.columns.find((coluna) => coluna.cards.some((c) => c.id === cardId));
  const card = origem?.cards.find((c) => c.id === cardId);
  if (origem === undefined || card === undefined || origem.stage_id === paraEtapa) return quadro;

  return {
    ...quadro,
    columns: quadro.columns.map((coluna) => {
      if (coluna.stage_id === origem.stage_id) {
        return {
          ...coluna,
          cards: coluna.cards.filter((c) => c.id !== cardId),
          totals: somar(coluna.totals, card, -1),
        };
      }
      if (coluna.stage_id === paraEtapa) {
        return {
          ...coluna,
          // No topo: é para onde a pessoa acabou de mandar, e é onde ela
          // espera encontrar o card ao olhar a coluna.
          //
          // A temperatura acompanha: o card acabou de entrar na etapa, e
          // deixá-la como estava mostraria "Frio" num card que a pessoa
          // acabou de mexer — o oposto do que a etiqueta significa.
          cards: [
            {
              ...card,
              days_in_stage: 0,
              is_stale: false,
              temperature: card.status === "open" ? ("hot" as const) : card.temperature,
            },
            ...coluna.cards,
          ],
          totals: somar(coluna.totals, card, 1),
        };
      }
      return coluna;
    }),
  };
}

function somar(totais: Quadro["columns"][number]["totals"], card: Card, sinal: 1 | -1) {
  const valor = card.value_cents ?? 0;
  const ponderado = Math.round((valor * (card.probability ?? 0)) / 100);
  return {
    count: Math.max(totais.count + sinal, 0),
    value_cents: Math.max(totais.value_cents + sinal * valor, 0),
    weighted_value_cents: Math.max(totais.weighted_value_cents + sinal * ponderado, 0),
  };
}
